import React, { useMemo } from "react";
import { Check, X } from "lucide-react";

const RULES = [
  { key: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "One uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One number (0–9)", test: (p) => /\d/.test(p) },
  { key: "special", label: "One special character (!@#$…)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export function getPasswordScore(password) {
  return RULES.reduce((acc, r) => acc + (r.test(password) ? 1 : 0), 0);
}

export function isStrongPassword(password) {
  return getPasswordScore(password) === RULES.length;
}

const TIER = [
  { label: "Too weak", bar: "bg-rose-500", width: "w-1/5", text: "text-rose-700" },
  { label: "Weak", bar: "bg-rose-400", width: "w-2/5", text: "text-rose-700" },
  { label: "Fair", bar: "bg-amber-500", width: "w-3/5", text: "text-amber-700" },
  { label: "Good", bar: "bg-emerald-500", width: "w-4/5", text: "text-emerald-700" },
  { label: "Strong", bar: "bg-emerald-600", width: "w-full", text: "text-emerald-700" },
];

export default function PasswordStrength({ password = "", testId = "password-strength" }) {
  const score = useMemo(() => getPasswordScore(password), [password]);
  const tier = TIER[Math.max(0, score - 1)] || TIER[0];
  const empty = password.length === 0;
  return (
    <div data-testid={testId} className="mt-2">
      <div className="h-1.5 w-full bg-black/5 rounded-sm overflow-hidden">
        <div className={`h-full transition-all ${empty ? "w-0" : `${tier.bar} ${tier.width}`}`} />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-[#525252]">Password strength</span>
        <span className={`text-xs font-medium ${empty ? "text-[#525252]" : tier.text}`} data-testid={`${testId}-tier`}>
          {empty ? "—" : tier.label}
        </span>
      </div>
      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {RULES.map((r) => {
          const ok = r.test(password);
          return (
            <li key={r.key} data-testid={`${testId}-rule-${r.key}`} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-[#525252]"}`}>
              {ok ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={2.5} className="text-[#999]" />}
              <span>{r.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
